/**
 * Dot Voting tool runner.
 * Ranks items by total vote count (number of dots received).
 */

import { readToolSettings } from "../lib/toolSettings";
import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Dot Voting.
 */
export function getDotVotingSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("dot-voting");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Dot Voting tool.
 * 
 * Note: This is a simplified implementation. In a real system, votes would be
 * collected through a UI interaction. For this runner, we assume votes are
 * stored in item attributes (e.g., item.attributes.votes or item.attributes.vote_count).
 * 
 * @param items - Items to rank by votes
 * @param _criteriaSet - CriteriaSet (not used by Dot Voting)
 * @param toolConfig - Tool configuration (dots_per_participant, etc.)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runDotVotingTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `dot-voting-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const { dotsPerParticipant } = readToolSettings(toolConfig);

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    // Use the arrange validation applyLens function
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Extract vote counts from items
  // In a real implementation, votes would come from a separate voting session
  // For this runner, we look for votes/vote_count in attributes
  const scoredItems: Array<{
    item_id: string;
    votes: number;
    rank: number;
    vote_percentage: number;
    is_tied: boolean;
  }> = [];
  const itemsWithVotes: Array<{ item: Item; votes: number }> = [];

  for (const item of filteredItems) {
    // Look for votes in attributes (votes, vote_count, or default to 0)
    const votes =
      item.attributes.votes !== undefined
        ? (typeof item.attributes.votes === "number" ? item.attributes.votes : Number(item.attributes.votes) || 0)
        : item.attributes.vote_count !== undefined
        ? (typeof item.attributes.vote_count === "number" ? item.attributes.vote_count : Number(item.attributes.vote_count) || 0)
        : 0;

    itemsWithVotes.push({ item, votes });
  }

  // Calculate total votes
  const totalVotes = itemsWithVotes.reduce((sum, { votes }) => sum + votes, 0);

  // Sort by votes (descending), then by label (ascending) for tie-breaking
  itemsWithVotes.sort((a, b) => {
    if (b.votes !== a.votes) {
      return b.votes - a.votes;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Assign ranks and build output
  let currentRank = 1;
  for (let i = 0; i < itemsWithVotes.length; i++) {
    const { item, votes } = itemsWithVotes[i];
    
    // If votes differ from previous, update rank
    if (i > 0 && itemsWithVotes[i - 1].votes !== votes) {
      currentRank = i + 1;
    }

    const votePercentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
    const isTied = i > 0 && itemsWithVotes[i - 1].votes === votes;

    scoredItems.push({
      item_id: item.id,
      votes: votes,
      rank: currentRank,
      vote_percentage: votePercentage,
      is_tied: isTied,
    });
  }

  return {
    items: scoredItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      total_votes: totalVotes,
      dots_per_participant: dotsPerParticipant,
    },
  };
}

